/* An animated, accessible presentation of the existing Shopify variant select. */
(() => {
  const instances = new Map();
  let sequence = 0;
  const icon = path => `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${path}"/></svg>`;

  class ConfigurationPicker {
    constructor(select) {
      this.select = select;
      this.opened = false;
      this.activeIndex = -1;
      this.controller = new AbortController();
      this.animations = new Set();
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.typeahead = '';
      this.prefix = `Configuration-${++sequence}`;
      this.label = select.labels?.[0];
      this.originalLabelFor = this.label?.htmlFor;
      this.originalTabIndex = select.getAttribute('tabindex');

      this.wrapper = document.createElement('div');
      this.wrapper.className = 'config-picker';
      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'config-trigger';
      this.trigger.id = this.prefix;
      this.trigger.setAttribute('role', 'combobox');
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-controls', this.prefix + '-options');
      this.value = document.createElement('span');
      this.value.className = 'config-value';
      this.chevron = document.createElement('span');
      this.chevron.className = 'config-chevron';
      this.chevron.innerHTML = icon('m6 9 6 6 6-6');
      this.trigger.append(this.value, this.chevron);
      this.popup = document.createElement('div');
      this.popup.id = this.prefix + '-options';
      this.popup.className = 'config-options';
      this.popup.setAttribute('role', 'listbox');
      this.popup.hidden = true;
      this.wrapper.append(this.trigger, this.popup);

      if (this.label) {
        this.label.id ||= this.prefix + '-label';
        this.trigger.setAttribute('aria-labelledby', this.label.id);
        this.popup.setAttribute('aria-labelledby', this.label.id);
      } else {
        this.trigger.setAttribute('aria-label', select.getAttribute('aria-label') || 'Configuration');
        this.popup.setAttribute('aria-label', 'Configuration');
      }
      this.refresh();
      select.after(this.wrapper);
      if (this.label) this.label.htmlFor = this.trigger.id;
      this.bind();
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(select, {childList:true, subtree:true, attributes:true, attributeFilter:['disabled','selected','data-available','data-price']});
      // Hide only after enhancement succeeds; the original still submits the variant ID.
      select.hidden = true;
      select.tabIndex = -1;
      select.dataset.configurationEnhanced = 'true';
    }

    enabled(index) { const option = this.select.options[index]; return !!option && !option.disabled && option.dataset.available !== 'false'; }
    enabledIndices() { return Array.from(this.select.options, (_, index) => index).filter(index => this.enabled(index)); }

    refresh() {
      this.options = Array.from(this.select.options);
      this.popup.replaceChildren();
      this.rows = this.options.map((option, index) => {
        const row = document.createElement('div');
        row.className = 'config-option';
        row.id = this.prefix + '-option-' + index;
        row.dataset.optionIndex = index;
        row.setAttribute('role', 'option');
        row.setAttribute('aria-disabled', String(!this.enabled(index)));
        const title = document.createElement('span');
        title.className = 'config-option-name';
        title.textContent = option.textContent.trim();
        const price = document.createElement('span');
        price.className = 'config-option-price';
        price.textContent = this.options.length > 1 ? option.dataset.price || '' : '';
        const check = document.createElement('span');
        check.className = 'config-check';
        check.innerHTML = icon('m5 12 4 4L19 6');
        row.append(title, price, check);
        this.popup.append(row);
        return row;
      });
      if (this.select.disabled && this.opened) this.close();
      this.trigger.disabled = this.select.disabled;
      this.value.textContent = this.options[this.select.selectedIndex]?.textContent.trim() || 'Select an option';
      if (!this.enabled(this.activeIndex)) this.activeIndex = this.enabled(this.select.selectedIndex) ? this.select.selectedIndex : this.enabledIndices()[0] ?? -1;
      this.paint();
    }

    paint() {
      this.rows.forEach((row, index) => {
        row.classList.toggle('is-selected', index === this.select.selectedIndex);
        row.classList.toggle('is-active', this.opened && index === this.activeIndex);
        row.setAttribute('aria-selected', String(index === (this.opened ? this.activeIndex : this.select.selectedIndex)));
      });
      if (this.opened && this.rows[this.activeIndex]) this.trigger.setAttribute('aria-activedescendant', this.rows[this.activeIndex].id);
      else this.trigger.removeAttribute('aria-activedescendant');
    }

    animate(element, frames, options) {
      if (this.reduced.matches || typeof element.animate !== 'function') return null;
      const animation = element.animate(frames, options);
      this.animations.add(animation);
      animation.finished.then(() => this.animations.delete(animation), () => this.animations.delete(animation));
      return animation;
    }

    open() {
      if (this.opened || this.trigger.disabled) return;
      this.menuAnimation?.cancel();
      this.opened = true;
      this.popup.hidden = false;
      this.popup.inert = false;
      this.popup.removeAttribute('aria-hidden');
      this.popup.style.pointerEvents = '';
      this.trigger.setAttribute('aria-expanded', 'true');
      this.activeIndex = this.enabled(this.select.selectedIndex) ? this.select.selectedIndex : this.enabledIndices()[0] ?? -1;
      this.paint();
      this.scrollActive();
      this.menuAnimation = this.animate(this.popup,
        [{opacity:0,transform:'translateY(-7px) scale(.985)'},{opacity:1,transform:'translateY(0) scale(1)'}],
        {duration:220,easing:'cubic-bezier(.2,.8,.2,1)'});
    }

    close() {
      if (!this.opened) return;
      this.opened = false;
      this.menuAnimation?.cancel();
      this.trigger.setAttribute('aria-expanded', 'false');
      this.popup.inert = true;
      this.popup.setAttribute('aria-hidden', 'true');
      this.popup.style.pointerEvents = 'none';
      this.paint();
      clearTimeout(this.typeaheadTimer);
      this.typeahead = '';
      this.menuAnimation = this.animate(this.popup,
        [{opacity:1,transform:'translateY(0)'},{opacity:0,transform:'translateY(-4px)'}],
        {duration:140,easing:'ease-in'});
      if (this.menuAnimation) this.menuAnimation.finished.then(() => {if (!this.opened) this.popup.hidden = true;}, () => {});
      else this.popup.hidden = true;
    }

    commit(index = this.activeIndex, feedback = true) {
      if (this.select.disabled || !this.enabled(index)) { this.close(); return; }
      const changed = this.select.selectedIndex !== index;
      this.select.selectedIndex = index;
      this.value.textContent = this.options[index].textContent.trim();
      this.close();
      this.paint();
      if (changed) {
        this.select.dispatchEvent(new Event('input', {bubbles:true}));
        this.select.dispatchEvent(new Event('change', {bubbles:true}));
      }
      if (feedback) {
        this.animate(this.value, [{opacity:.4,transform:'translateY(3px)'},{opacity:1,transform:'translateY(0)'}], {duration:240,easing:'ease-out'});
        this.animate(this.trigger, [{boxShadow:'0 0 0 0 rgba(227,44,37,.22)'},{boxShadow:'0 0 0 4px rgba(227,44,37,0)'}], {duration:400,easing:'ease-out'});
      }
    }

    scrollActive() {
      const row = this.rows[this.activeIndex];
      if (!row) return;
      // Scroll only the option list; never move the page when a menu opens.
      if (row.offsetTop < this.popup.scrollTop) this.popup.scrollTop = row.offsetTop;
      else if (row.offsetTop + row.offsetHeight > this.popup.scrollTop + this.popup.clientHeight) this.popup.scrollTop = row.offsetTop + row.offsetHeight - this.popup.clientHeight;
    }

    activate(index) { if (this.enabled(index)) {this.activeIndex = index;this.paint();this.scrollActive();} }

    onKey(event) {
      const key = event.key;
      if (event.altKey && key === 'ArrowUp') {if (this.opened) {event.preventDefault();this.commit();}return;}
      if (key === 'Tab') {if (this.opened) this.commit(this.activeIndex, false);return;}
      if (key === 'Escape') {if (this.opened) {event.preventDefault();event.stopPropagation();this.close();}return;}
      if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        if (this.opened) this.commit(); else this.open();
        return;
      }
      if (['ArrowDown','ArrowUp','Home','End'].includes(key)) {
        event.preventDefault();
        const wasOpen = this.opened;
        this.open();
        const indices = this.enabledIndices();
        if (key === 'Home') this.activate(indices[0]);
        else if (key === 'End') this.activate(indices.at(-1));
        else if (wasOpen) {
          const current = indices.indexOf(this.activeIndex);
          this.activate(indices[Math.max(0, Math.min(indices.length - 1, current + (key === 'ArrowDown' ? 1 : -1)))]);
        }
        return;
      }
      if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        this.open();
        clearTimeout(this.typeaheadTimer);
        this.typeahead += key.toLocaleLowerCase();
        const repeated = [...this.typeahead].every(character => character === this.typeahead[0]);
        const query = repeated ? this.typeahead[0] : this.typeahead;
        const start = repeated ? this.activeIndex + 1 : this.activeIndex;
        for (let offset = 0; offset < this.options.length; offset++) {
          const index = (Math.max(0, start) + offset) % this.options.length;
          if (this.enabled(index) && this.options[index].textContent.trim().toLocaleLowerCase().startsWith(query)) {this.activate(index);break;}
        }
        this.typeaheadTimer = setTimeout(() => {this.typeahead = '';}, 700);
      }
    }

    bind() {
      const listen = (target, type, callback) => target?.addEventListener(type, callback, {signal:this.controller.signal});
      listen(this.trigger, 'click', () => this.opened ? this.close() : this.open());
      listen(this.trigger, 'keydown', event => this.onKey(event));
      listen(this.popup, 'mousedown', event => event.preventDefault());
      listen(this.popup, 'click', event => {
        const row = event.target.closest('[data-option-index]');
        if (!row || !this.enabled(Number(row.dataset.optionIndex))) return;
        this.commit(Number(row.dataset.optionIndex));
        this.trigger.focus({preventScroll:true});
      });
      listen(document, 'pointerdown', event => {
        if (this.opened && !this.wrapper.contains(event.target)) this.commit(this.activeIndex, false);
      });
      listen(this.trigger, 'blur', () => queueMicrotask(() => {
        if (this.opened && !this.wrapper.contains(document.activeElement)) this.commit(this.activeIndex, false);
      }));
      listen(this.select, 'change', () => {this.value.textContent = this.select.selectedOptions[0]?.textContent.trim() || 'Select an option';this.activeIndex = this.select.selectedIndex;this.paint();});
      listen(this.select.form, 'reset', () => setTimeout(() => {this.close();this.refresh();}, 0));
      listen(this.reduced, 'change', () => {
        if (this.reduced.matches) {this.animations.forEach(animation => animation.cancel());if (!this.opened) this.popup.hidden = true;}
      });
    }

    destroy() {
      this.controller.abort();
      this.observer.disconnect();
      this.animations.forEach(animation => animation.cancel());
      clearTimeout(this.typeaheadTimer);
      this.wrapper.remove();
      this.select.hidden = false;
      if (this.originalTabIndex === null) this.select.removeAttribute('tabindex'); else this.select.setAttribute('tabindex', this.originalTabIndex);
      if (this.label) this.label.htmlFor = this.originalLabelFor;
      delete this.select.dataset.configurationEnhanced;
      instances.delete(this.select);
    }
  }

  function initialize(scope = document) {
    scope.querySelectorAll('[data-variant-select]').forEach(select => {
      if (instances.has(select)) return;
      instances.set(select, new ConfigurationPicker(select));
    });
  }
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((picker, select) => {if (event.target.contains(select)) picker.destroy();});
  });
})();
